import React, { useEffect, useMemo, useState } from 'react'
import { useQueryDrivers } from '@/lib/api/company/drivers'
import { Driver } from '@/lib/api/company/drivers/types'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableRadio from '@/modules/drivers/shared/tableItems/TableRadio'
import TableCheckbox from '@/modules/drivers/shared/tableItems/TableCheckbox'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import { Button } from '@/ui/button'
import { Checkbox } from '@/ui'
import { TextField } from '@/ui/form/controls/TextField'
import { FaIcon, faUserSolid } from '@/lib/fa'

type DriverPickerProps = {
  companyId: string

  /** Initial selection */
  initial?: Driver[]

  /** When false -> radio (single). When true -> checkboxes (multiple). */
  multiple?: boolean

  /** Called with the new selection list */
  onSubmit: (selected: Driver[]) => void
  onCancel?: () => void

  /** Header texts */
  title?: string
  subtitleLeft?: string
  subtitleEntity?: string
  subtitleRight?: string

  /** Buttons */
  submitLabel?: string
  cancelLabel?: string

  disabled?: boolean
  className?: string
}

const EMPTY_DRIVERS: Driver[] = []

function normalize(str: string) {
  return str.toLowerCase().trim()
}

function getDriverDisplayName(d: Driver) {
  return d.fullName?.trim()
    ? d.fullName
    : `${d.surname ?? ''} ${d.name ?? ''}`.trim()
}

function getDriverCardNumber(d: Driver) {
  if (d.cardNumber?.value) return d.cardNumber.value
  if (d.driverCard?.cardNumber?.value) return d.driverCard.cardNumber.value
  if (d.driverCards?.length) return d.driverCards[0]?.cardNumber?.value ?? null
  return null
}

export function DriverPicker({
  companyId,
  initial,
  multiple = false,
  onSubmit,
  onCancel,
  title: titleProp,
  subtitleLeft = '',
  subtitleEntity = '',
  subtitleRight = '',
  submitLabel: submitLabelProp,
  cancelLabel: cancelLabelProp,
  disabled = false,
  className,
}: DriverPickerProps) {
  const { t } = useLocalize()
  const title = titleProp ?? t('dashboard.drivers')
  const submitLabel = submitLabelProp ?? t('common.save')
  const cancelLabel = cancelLabelProp ?? t('common.cancel')

  const { data: drivers, isLoading, isError } = useQueryDrivers({ companyId })

  // Avoid "Maximum update depth exceeded" when initial is not provided
  const initialDrivers = initial ?? EMPTY_DRIVERS

  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    () => new Set(initialDrivers.map((d) => d.id)),
  )
  const [query, setQuery] = useState('')
  const [activeOnly, setActiveOnly] = useState(true)

  // Stable signature for initial selection (order-independent)
  const initialIdsKey = useMemo(() => {
    const ids = initialDrivers.map((d) => d.id).slice().sort((a, b) => a - b)
    return ids.join(',')
  }, [initialDrivers])

  // Sync selection only when initial IDs truly changed
  useEffect(() => {
    const next = new Set(initialDrivers.map((d) => d.id))

    setSelectedIds((prev) => {
      if (prev.size !== next.size) return next
      for (const id of prev) if (!next.has(id)) return next
      return prev
    })
  }, [initialIdsKey, initialDrivers])

  const allDrivers = useMemo(() => drivers ?? [], [drivers])

  const filteredDrivers = useMemo(() => {
    const q = normalize(query)

    // Nieaktywnego kierowcy nie da sie sensownie znalezc w liscie 200+ pozycji, wiec
    // domyslnie pokazujemy aktywnych - ale nigdy nie chowamy kogos juz zaznaczonego,
    // bo zniknicie wiersza wyglada jak utrata wyboru.
    const visible = allDrivers.filter(
      (d) => !activeOnly || d.isActive || selectedIds.has(d.id),
    )

    if (!q) return visible

    return visible.filter((d) => {
      const name = normalize(getDriverDisplayName(d))
      const card = normalize(getDriverCardNumber(d) ?? '')
      return name.includes(q) || card.includes(q)
    })
  }, [allDrivers, query, activeOnly, selectedIds])

  const columns = useMemo<TableHeaderColumn[]>(
    () => [
      { title: t('shared.driver-picker.name-header'), key: 'fullName', type: 'string', sort: true },
      { title: t('shared.picker.db-status'), key: 'isActive', type: 'string', sort: true },
      { title: t('shared.driver-picker.card-number'), key: 'cardNumber', type: 'string', sort: true },
    ],
    [t],
  )

  const sortableRows = useMemo(
    () =>
      filteredDrivers.map((d) => ({
        driver: d,
        fullName: getDriverDisplayName(d),
        isActive: d.isActive,
        cardNumber: getDriverCardNumber(d) ?? '',
      })),
    [filteredDrivers],
  )

  const { sortedData, sortConfig, handleHeaderClick } = useSortableData({
    data: sortableRows,
    tableColumns: columns,
  })

  const selectedDrivers = useMemo(
    () => allDrivers.filter((d) => selectedIds.has(d.id)),
    [allDrivers, selectedIds],
  )

  const selectedLabel = useMemo(() => {
    if (selectedDrivers.length === 0) return '—'
    // Match screenshot style: for single pick show one full name; for multiple show joined
    if (selectedDrivers.length === 1) return getDriverDisplayName(selectedDrivers[0])
    return selectedDrivers.map(getDriverDisplayName).join(', ')
  }, [selectedDrivers])

  const toggle = (driverId: number) => {
    if (disabled) return

    setSelectedIds((prev) => {
      const next = new Set(prev)

      if (multiple) {
        if (next.has(driverId)) next.delete(driverId)
        else next.add(driverId)
        return next
      }

      // single selection: radio behavior
      return new Set<number>([driverId])
    })
  }

  const handleSubmit = () => {
    if (disabled) return
    onSubmit(selectedDrivers)
  }

  const handleCancel = () => {
    onCancel?.()
  }

  return (
    <div className={['tw-w-full', className].filter(Boolean).join(' ')}>
      {/* Header */}
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
        <div className="tw-min-w-0">
          <div className="tw-text-base tw-font-semibold tw-text-zinc-900">{title}</div>

          <div className="tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-gap-1 tw-text-xs tw-text-zinc-500">
            <span>{subtitleLeft}</span>
            {subtitleEntity ? (
              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-zinc-700">
                <span className="tw-inline-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded tw-bg-zinc-100 tw-text-[10px]">
                  ✓
                </span>
                <span className="tw-font-medium">{subtitleEntity}</span>
              </span>
            ) : null}
            <span>{subtitleRight}</span>
          </div>
        </div>

        {/* <button
          type="button"
          className="tw-rounded tw-p-2 tw-text-zinc-500 hover:tw-bg-zinc-100 hover:tw-text-zinc-700 disabled:tw-opacity-50"
          onClick={handleCancel}
          disabled={disabled || !onCancel}
          aria-label="Zamknij"
          title="Zamknij"
        >
          {/-* Close icon *-/}
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M4 4l8 8M12 4L4 12"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button> */}
      </div>

      {/* Search */}
      <div className="tw-mt-3">
        <TextField
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('shared.driver-picker.search-placeholder')}
          disabled={disabled}
        />
      </div>

      <div className="tw-mt-2 tw-flex tw-items-center tw-gap-2">
        <label className="tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm tw-text-content-base">
          <Checkbox
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
            disabled={disabled}
          />
          {t('shared.driver-picker.active-only')}
        </label>
      </div>

      {/* Table */}
      <div className="tw-mt-3 tw-overflow-hidden tw-rounded tw-border tw-border-base-300">
        <div className="tw-max-h-[340px] tw-overflow-auto">
          <table className="tw-w-full tw-table-auto tw-border-collapse tw-pt-2">
            <TableHeader
            fontSize="md"
            rounded
            bolder
            columns={columns}
            sortConfig={sortConfig}
            onHeaderClick={handleHeaderClick}
            toggleAll={(checked: boolean) => {
              if (disabled || !multiple) return
              setSelectedIds(
                checked ? new Set(sortedData.map((r) => r.driver.id)) : new Set<number>(),
              )
            }}
            isSelectedAny={multiple && selectedIds.size > 0}
            disabledToggle={!multiple}
            sticky
            />
            <tbody>
              {isLoading || isError || sortedData.length === 0 ? (
                <tr>
                  <td colSpan={4} className="tw-px-3 tw-py-6 tw-text-sm tw-text-content-base">
                    {isLoading
                      ? t('shared.driver-picker.loading')
                      : isError
                        ? t('shared.driver-picker.error')
                        : t('shared.picker.no-results')}
                  </td>
                </tr>
              ) : (
                sortedData.map((row) => {
                  const d = row.driver
                  const checked = selectedIds.has(d.id)

                  return (
                    <tr
                      key={d.id}
                      className={`notches-border--right tw-cursor-pointer tw-border-b tw-border-b-base-300 hover:tw-bg-primary/10 ${checked ? 'tw-bg-primary/10 tw-font-medium' : 'tw-bg-white'}`}
                      onClick={() => toggle(d.id)}
                    >
                      {multiple ? (
                        <TableCheckbox
                          checked={checked}
                          onChange={() => toggle(d.id)}
                          disabled={disabled}
                        />
                      ) : (
                        <TableRadio
                          checked={checked}
                          onClick={() => toggle(d.id)}
                          disabled={disabled}
                        />
                      )}

                      <TableTextRow icon={<FaIcon icon={faUserSolid} className="tw-h-3.5 tw-w-3.5" />}>
                        {row.fullName}
                      </TableTextRow>

                      <TableTextRow bolder={false}>
                        {row.isActive ? t('common.active') : t('common.inactive')}
                      </TableTextRow>

                      <TableTextRow bolder={false}>{row.cardNumber || '—'}</TableTextRow>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-t tw-border-base-300 tw-bg-white tw-px-3 tw-py-3">
          <div className="tw-min-w-0 tw-text-xs tw-text-content-base">
            {t('shared.driver-picker.selected')}:{' '}
            <span className="tw-font-medium">{selectedLabel}</span>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2">
            {onCancel ? (
              <Button
                text={cancelLabel}
                variant="neutral"
                mode="outline"
                size="sm"
                onClick={handleCancel}
                disabled={disabled}
              />
            ) : null}

            <Button
              text={submitLabel}
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={disabled || selectedDrivers.length === 0}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
